import { Col, Container, Row, Spinner } from 'reactstrap'
import {
  GRID_COL_HALF_MAP,
  GRID_COL_MAP,
  JSON_TEMPLATES,
} from '../../config/templateConfig'

// Import widget components
import SectionAttendanceWidget from 'features/private-views/dashboard/SectionAttendanceWidget'
import SectionCards from 'features/private-views/dashboard/SectionCards'
import SectionCashFlowWidget from 'features/private-views/dashboard/SectionCashFlowWidget'
import SectionEventWidget from 'features/private-views/dashboard/SectionEventWidget'
import SectionWhistleblowingCards from 'features/private-views/dashboard/SectionWhistleblowingCards'
import TableDashboard from 'features/private-views/dashboard/TableDashboard'
import GenericLinkWidget from './GenericLinkWidget'
import { WidgetUangBelanja, WidgetUangUsaha, WidgetTalangin } from './FinancialWidgets'

// Widget Registry for JSON-based system
const WIDGET_REGISTRY = {
  SECTION_CARDS: (props) => <SectionCards mode={props.mode} {...props} />, // Keeping for legacy config support
  CARD_UANG_BELANJA: (props) => <WidgetUangBelanja {...props} useDefaultStyle={true} />,
  CARD_UANG_USAHA: (props) => <WidgetUangUsaha {...props} useDefaultStyle={true} />,
  CARD_TALANGIN: (props) => <WidgetTalangin {...props} useDefaultStyle={true} />,
  NEW_CARD_UANG_BELANJA: (props) => <WidgetUangBelanja {...props} useDefaultStyle={false} />,
  NEW_CARD_UANG_USAHA: (props) => <WidgetUangUsaha {...props} useDefaultStyle={false} />,
  NEW_CARD_TALANGIN: (props) => <WidgetTalangin {...props} useDefaultStyle={false} />,
  TRANSAKSI_AKTIF: (props) => (
    <TableDashboard title="Transaksi Aktif" mode={props.mode} {...props} />
  ),
  ANGGOTA: (props) => (
    <SectionAttendanceWidget displayCount={props.params?.disp} mode={props.mode} {...props} />
  ),
  PENGADUAN: (props) => <SectionWhistleblowingCards mode={props.mode} {...props} />,
  ABSENSI: (props) => <SectionAttendanceWidget mode={props.mode} {...props} />,
  EVENT: (props) => <SectionEventWidget mode={props.mode} {...props} />,
  CASHFLOW: (props) => <SectionCashFlowWidget mode={props.mode} {...props} />,
  GENERIC_LINK: (props) => (
    <GenericLinkWidget
      title={props.params?.title}
      description={props.params?.description}
      icon={props.params?.icon}
      color={props.params?.color}
      url={props.params?.url}
      half={props.size === 'half'}
      mode={props.mode}
      {...props}
    />
  ),
}

// Helper function to resolve column properties
const resolveColProps = (widget, grid) => {
  // Complex widgets that contain inner rows MUST span the full width
  const ALWAYS_FULL_WIDTH_TYPES = ['SECTION_CARDS', 'CASHFLOW'];
  
  if (widget.fullWidth || ALWAYS_FULL_WIDTH_TYPES.includes(widget.type)) {
    return { xs: 12, md: 12, lg: 12 };
  }
  
  // Custom sizing for small modes so they don't look like giant empty squares
  if (widget.mode === 'tiny' || widget.mode === 'tiny-square') {
    return { xs: 12, sm: 6, md: 4, lg: 3 } // allows them to take grid space and stack instead of auto-shrink
  }
  if (widget.mode === 'compact') {
     return { xs: 12, md: 6, lg: 4, xl: 3 } // slightly smaller than standard
  }
  if (widget.size === 'half' && grid >= 3) {
    return GRID_COL_HALF_MAP[grid] || GRID_COL_HALF_MAP[3]
  }
  return GRID_COL_MAP[grid] || GRID_COL_MAP[3]
}

const JsonWidgetRenderer = ({ config, templateKey }) => {
  // Resolve configuration based on priority
  let resolvedConfig = null

  if (config) {
    // Priority 1: Direct config object
    resolvedConfig = config
  } else if (templateKey) {
    // Priority 2: Template key
    resolvedConfig = JSON_TEMPLATES[templateKey]
  }

  // JSON-based rendering
  if (!resolvedConfig || (!resolvedConfig.widgets && !resolvedConfig.categories)) {
    return (
      <Container fluid className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
        <Spinner color="primary" />
      </Container>
    )
  }

  const grid = resolvedConfig.settings?.grid || 3
  const categories = resolvedConfig.categories
  const widgets = resolvedConfig.widgets

  const renderWidgetList = (widgetList) => {
    const groupedWidgets = [];
    let i = 0;
    const isGroupableWidget = (w) => ['tiny', 'tiny-square', 'compact', 'secondary'].includes(w.mode);

    while (i < widgetList.length) {
      if (isGroupableWidget(widgetList[i])) {
        let group = [widgetList[i]];
        let j = i + 1;
        const MAX_GROUP_HEIGHT_UNITS = 6; 
        while (j < widgetList.length && isGroupableWidget(widgetList[j])) {
          group.push(widgetList[j]);
          j++;
        }
        
        let currentGroup = [];
        let currentWeight = 0;
        
        for (let k = 0; k < group.length; k++) {
           const w = group[k];
           
           let weight = 6;
           if (w.mode === 'compact') weight = 3;
           else if (w.mode === 'secondary') weight = 4;
           else if (w.mode === 'tiny' || w.mode === 'tiny-square') weight = 2;
           
           if (currentWeight + weight > MAX_GROUP_HEIGHT_UNITS) {
             groupedWidgets.push({ _isWidgetGroup: true, widgets: currentGroup });
             currentGroup = [w];
             currentWeight = weight;
           } else {
             currentGroup.push(w);
             currentWeight += weight;
           }
        }
        if (currentGroup.length > 0) {
           groupedWidgets.push({ _isWidgetGroup: true, widgets: currentGroup });
        }
        
        i = j;
      } else {
        groupedWidgets.push(widgetList[i]);
        i++;
      }
    }

    return (
      <Row className="g-3 align-items-stretch">
        {groupedWidgets.map((item, index) => {
          if (item._isWidgetGroup) {
            const groupColProps = resolveColProps({ mode: 'secondary' }, grid);
            return (
              <Col
                key={`widget-group-${index}`}
                {...groupColProps}
                data-testid={`homepage-widget-group-${index}`}
              >
                <div className="d-flex flex-row flex-wrap" style={{ gap: '1rem', alignContent: 'space-between', height: '100%' }}>
                  {item.widgets.map((tw, twIdx) => {
                    const WidgetCmp = WIDGET_REGISTRY[tw.type];
                    if (!WidgetCmp) return null;
                    const isSquare = tw.mode === 'tiny-square';
                    return (
                      <div key={twIdx} style={{ width: isSquare ? 'calc(33.33% - 0.75rem)' : '100%' }}>
                        <WidgetCmp {...tw} />
                      </div>
                    );
                  })}
                </div>
              </Col>
            );
          }

          const widget = item;
          const WidgetComponent = WIDGET_REGISTRY[widget.type]

          if (!WidgetComponent) {
            console.warn(`Widget type not found: ${widget.type}`)
            return null
          }

          const colProps = resolveColProps(widget, grid)

          let finalColProps = colProps
          if (
            !widget.fullWidth &&
            [
              'NEW_CARD_UANG_BELANJA',
              'NEW_CARD_UANG_USAHA',
              'NEW_CARD_TALANGIN',
              'CARD_UANG_BELANJA',
              'CARD_UANG_USAHA',
              'CARD_TALANGIN',
            ].includes(widget.type)
          ) {
            finalColProps = { xs: 12, md: 6, lg: 4 }
          }

          return (
            <Col
              key={`${widget.type}-${index}`}
              {...finalColProps}
              data-testid={`homepage-widget-${widget.type}-${index}`}
            >
              <div className="w-100 h-auto">
                <WidgetComponent {...widget} />
              </div>
            </Col>
          )
        })}
      </Row>
    )
  }

  const hasCategories = categories && categories.length > 0;
  const hasWidgets = widgets && widgets.length > 0;

  if (!hasCategories && !hasWidgets) {
    return (
      <div className="text-center py-5">
        <p className="text-muted">Tidak ada widget untuk ditampilkan</p>
      </div>
    )
  }

  return (
    <div className="homepage-widgets-container" style={{ overflowX: 'hidden' }}>
      <style>{`
        .homepage-widgets-container,
        .homepage-widgets-container * {
          scrollbar-width: none !important;
          -ms-overflow-style: none !important;
        }
        .homepage-widgets-container *::-webkit-scrollbar {
          display: none !important;
          width: 0 !important;
          height: 0 !important;
        }
      `}</style>

      {/* 1. Render Uncategorized / Loose Widgets First */}
      {hasWidgets && (
        <div className="mb-4">
          {renderWidgetList(widgets)}
        </div>
      )}

      {/* 2. Render Categories Below */}
      {hasCategories && categories.map((cat, catIdx) => (
        <div
          key={`cat-${catIdx}`}
          className={(catIdx > 0 || hasWidgets) ? 'mt-4 pt-2' : ''}
          data-testid={`homepage-category-${catIdx}`}
        >
          {cat.title && (
            <h5 className="mb-3 fw-bold ps-1" style={{ color: '#1e293b' }}>{cat.title}</h5>
          )}
          {renderWidgetList(cat.widgets || [])}
        </div>
      ))}
    </div>
  )
}

export default JsonWidgetRenderer
